import React, { useEffect, useReducer, useRef, useState } from 'react';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import Image from '@tiptap/extension-image';
import { FaTrash } from 'react-icons/fa';

/**
 * The wiki image node: TipTap's Image with a drag-to-resize handle, a delete
 * button, and the chosen width stored as `style="width: Npx"`.
 *
 * ONE definition, shared by the client wiki's editor (`WikiPageEditor.js`) and
 * the TME Wiki's (`components/tmeWiki/TmeWikiPageEditor.js`). It lived inside
 * WikiPageEditor.js until 2026-09-02, when the TME editor - built as "a second
 * editor with an identical formatting configuration" - turned out to have
 * been given TipTap's PLAIN image instead: no handles, no stored width. Bill
 * found it on the first preview use. Moved here so the two editors cannot
 * differ on it again; `wikiSanitise.test.js` still reads WikiPageEditor.js's
 * own `HTMLAttributes` blocks for the class allow-list, which this node never
 * carried (it writes `style` only).
 *
 * ⚠️ The width is written as a `style`, and read back out of it. TipTap's
 * default parse for an attribute called `width` looks for a width ATTRIBUTE,
 * finds none, and resets to null - so a resized image reopened at full width
 * even though the size had saved correctly. `parseHTML` below reads the style.
 * `px` widths are what the sanitiser's `ALLOWED_STYLE_PROPS.width` admits.
 */
const ResizableImageComponent = ({
    node, updateAttributes, selected, deleteNode, getPos, editor, extension
}) => {
    // Absent for the CLIENT wiki, which has no media-delete route at all. When
    // absent the bin behaves exactly as it always did: remove the node, leave
    // the file (TWM §7.1).
    const onDeleteMedia = extension?.options?.onDeleteMedia;
    const [isResizing, setIsResizing] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startWidth, setStartWidth] = useState(0);
    const imageRef = useRef(null);

    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        // Only start resize if we have a valid image reference with measurable width
        const currentWidth = imageRef.current?.offsetWidth;
        if (!currentWidth) {
            console.error('Cannot resize: image reference not available or has no width');
            return;
        }
        setIsResizing(true);
        setStartX(e.clientX);
        setStartWidth(currentWidth);
    };

    // ⛔ `editor.isEditable` IS NOT REACTIVE IN A NODE VIEW (TWM §7.3.1, test
    // 27b). ReactNodeViewRenderer re-renders on node change and on
    // select/deselect, and never reads isEditable - so `setEditable(false)`
    // alone left the bin on screen, which test 27a proved before this existed.
    // `setEditable` emits `update` (@tiptap/core: it calls setOptions then
    // emit('update', …)), so ONE narrow subscription is enough; `transaction`
    // would fire on every keystroke for every image on the page.
    const [, forceRender] = useReducer(count => count + 1, 0);
    useEffect(() => {
        if (!editor) return undefined;
        const rerender = () => forceRender();
        editor.on('update', rerender);
        return () => { editor.off('update', rerender); };
    }, [editor]);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            const diff = e.clientX - startX;
            const newWidth = Math.max(100, startWidth + diff);
            updateAttributes({ width: newWidth });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, startX, startWidth, updateAttributes]);

    return (
        <NodeViewWrapper className="relative inline-block">
            <div className={`relative inline-block ${selected ? 'ring-2 ring-blue-500' : ''}`}>
                <img
                    ref={imageRef}
                    src={node.attrs.src}
                    alt={node.attrs.alt}
                    style={{ width: node.attrs.width ? `${node.attrs.width}px` : 'auto' }}
                    className="max-w-full h-auto rounded-md"
                    draggable={false}
                />
                {/* ⚠️ `editor.isEditable` AS WELL AS `selected` (TWM §7.3.1,
                    K2). These controls sat outside every lock the pane has: the
                    TME delete flow makes the editor read-only for the duration
                    of its ~7 s save, and without this a selected image still
                    showed its bin - a second click stacking a second confirm on
                    an in-flight save, a second deleteNode() and two saves racing
                    on one page.version.

                    ⚠️ REACT DOES NOT WATCH `isEditable` ON ITS OWN:
                    ReactNodeViewRenderer re-renders on node change and on
                    select/deselect, and never subscribes to setEditable. The
                    `update` subscription above is what makes this line respond
                    to it at all - test 27a proved the bin stayed on screen
                    before that existed. The flow's own deleteNode() also
                    unmounts the selected node, so any image selected afterwards
                    renders fresh and reads false - but
                    the GUARANTEE is the pane's re-entry ref, not this gate. */}
                {selected && editor.isEditable && (
                    <>
                        {/* Resize handle */}
                        <div
                            className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize bg-blue-500 opacity-50 hover:opacity-75"
                            onMouseDown={handleMouseDown}
                            title="Drag to resize"
                        />
                        {/* Delete button */}
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // ⚠️ node AND getPos, not just the src: the
                                // caller captures node.toJSON() and the position
                                // so it can put the image back if the save
                                // fails. It also owns the WHOLE sequence from
                                // here - including removing the node, which must
                                // not happen if the user cancels the dialog, and
                                // including calling deleteNode() itself for an
                                // image that is not ours to delete.
                                if (onDeleteMedia) {
                                    return onDeleteMedia({
                                        src: node.attrs.src, node, getPos, deleteNode
                                    });
                                }
                                deleteNode();
                            }}
                            className="absolute top-1 right-5 bg-red-500 text-white p-1 rounded-full hover:bg-red-600 shadow-lg"
                            title="Delete image"
                        >
                            <FaTrash className="w-3 h-3" />
                        </button>
                    </>
                )}
            </div>
        </NodeViewWrapper>
    );
};

const ResizableImage = Image.extend({
    /**
     * ⚠️ AN OPTION, ABSENT BY DEFAULT, BECAUSE THIS NODE IS SHARED (TWM §3.1).
     * The client wiki's editor imports it too and has NO media-delete route, so
     * a hard-coded fetch in here would make its bin call an endpoint that does
     * not exist for it. Only the TME editor configures this.
     *
     * ⚠️ WHATEVER IS PASSED MUST BE A STABLE WRAPPER OVER A REF, never a raw
     * callback (TWM §7.1.1): `useEditor` is called with no dependency array, so
     * the extension list is built ONCE and a raw callback would be captured from
     * the FIRST render - when the page is still null.
     */
    addOptions() {
        return {
            ...this.parent?.(),
            onDeleteMedia: null
        };
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => {
                    const match = (element.style?.width || '').match(/^(\d+(?:\.\d+)?)px$/);
                    return match ? parseFloat(match[1]) : null;
                },
                renderHTML: (attributes) => {
                    if (!attributes.width) {
                        return {};
                    }
                    return {
                        style: `width: ${attributes.width}px`,
                    };
                },
            },
        };
    },
    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageComponent);
    },
});

export default ResizableImage;
